import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Input,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import {
  CONTRACT_TYPES,
  PLACEHOLDERS,
  contractQuillFormats,
  contractQuillModules
} from "./contractTypes";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./Contracts.css";

const { Text, Paragraph } = Typography;
const authHeader = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

/**
 * Lets the service provider replace the built-in template of any contract type with their
 * own wording. New contracts of that type start from it; existing contracts are unaffected.
 */
const ContractTemplates = () => {
  const { t } = useTranslation("dashboard");
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(CONTRACT_TYPES[0].key);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const quillRef = useRef(null);
  const isMobile = useIsMobile();

  const load = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/contracts/templates`, {
        headers: authHeader()
      });
      setTemplates(res.data.templates);
      return res.data.templates;
    } catch (err) {
      generalErrorAlert(err);
      return null;
    }
  };

  const select = (key, source = templates) => {
    setSelected(key);
    setTitle(source?.[key]?.title || "");
    setBody(source?.[key]?.body || "");
    setDirty(false);
  };

  useEffect(() => {
    if (open) load().then(loaded => loaded && select(selected, loaded));
  }, [open]);

  const trySelect = key => {
    if (!dirty) return select(key);
    Modal.confirm({
      title: t("Discard your changes to this template?"),
      okText: t("Discard"),
      cancelText: t("Cancel"),
      onOk: () => select(key)
    });
  };

  const insertPlaceholder = name => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range ? range.index : editor.getLength(), `{{${name}}}`, "user");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/templates/${selected}`,
        { title, body },
        { headers: authHeader() }
      );
      Success(t("Template saved"));
      const loaded = await load();
      select(selected, loaded);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/contracts/templates/${selected}`, {
        headers: authHeader()
      });
      Success(t("Template reset to the default"));
      const loaded = await load();
      select(selected, loaded);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  return (
    <>
      <Button icon={<FileTextOutlined />} onClick={() => setOpen(true)}>
        {t("Templates")}
      </Button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={t("Contract templates")}
        width={1100}
        centered
        destroyOnClose
        className="contract-templates"
        footer={
          <Flex justify="end" gap={8} wrap="wrap">
            {templates?.[selected]?.isCustom && (
              <Popconfirm
                title={t("Reset this template to the default?")}
                okText={t("Reset")}
                cancelText={t("Cancel")}
                onConfirm={reset}
              >
                <Button>{t("Reset to default")}</Button>
              </Popconfirm>
            )}
            <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>
              {t("Save template")}
            </Button>
          </Flex>
        }
      >
        <Paragraph type="secondary">
          {t(
            "Adjust the wording new contracts start from. Placeholders in double braces are filled in from the contract form. Existing contracts do not change."
          )}
        </Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            {isMobile ? (
              <Select
                className="w-100"
                value={selected}
                onChange={trySelect}
                aria-label={t("Contract type")}
                options={CONTRACT_TYPES.map(type => ({
                  value: type.key,
                  label: templates?.[type.key]?.isCustom
                    ? `${t(type.label)} · ${t("Own")}`
                    : t(type.label)
                }))}
              />
            ) : (
              <Menu
                mode="inline"
                selectedKeys={[selected]}
                onClick={({ key }) => trySelect(key)}
                items={CONTRACT_TYPES.map(type => ({
                  key: type.key,
                  label: (
                    <Flex justify="space-between" align="center" gap={4}>
                      <span>{t(type.label)}</span>
                      {templates?.[type.key]?.isCustom && <Tag color="blue">{t("Own")}</Tag>}
                    </Flex>
                  )
                }))}
              />
            )}
          </Col>
          <Col xs={24} md={18}>
            <Input
              className="mb-2"
              value={title}
              addonBefore={t("Title")}
              onChange={event => {
                setTitle(event.target.value);
                setDirty(true);
              }}
            />
            <div className="contract-editor contract-editor--template">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={contractQuillModules}
                formats={contractQuillFormats}
                value={body}
                onChange={(value, _delta, source) => {
                  setBody(value);
                  if (source === "user") setDirty(true);
                }}
              />
            </div>
            <div className="mt-2">
              <Text type="secondary">{t("Insert placeholder:")} </Text>
              <Flex wrap="wrap" gap={4} className="mt-1">
                {PLACEHOLDERS.map(name => (
                  <Tooltip key={name} title={t("Insert at the cursor")}>
                    <Tag className="contract-placeholder" onClick={() => insertPlaceholder(name)}>
                      {`{{${name}}}`}
                    </Tag>
                  </Tooltip>
                ))}
              </Flex>
              <Text type="secondary" className="d-block mt-2 contract-legal-note">
                {/* The markers go in as values: written into the key, i18next would read
                    them as its own interpolation. */}
                {t(
                  "Text between {{open}} and {{close}} only appears in fixed-term contracts, text between {{openNot}} and {{close}} only in open-ended ones. The same works for probation.",
                  { open: "{{#fixedTerm}}", openNot: "{{^fixedTerm}}", close: "{{/fixedTerm}}" }
                )}
              </Text>
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ContractTemplates;
